import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  Send,
  Users,
  Plus,
  Lock,
  Trash2,
  MoreHorizontal,
  PenSquare,
  ChevronRight,
  Image,
  X,
  ThumbsUp,
  UserPlus,
  UserCheck,
  UserMinus,
  Search,
  Circle,
  Bell,
  Eye,
  Check,
  Clock
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { FounderBadge } from '../components/FounderBadge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UserActionButtons({ targetUserId, targetName, currentUserId, relationships, onUpdate, compact = false }) {
  const [loading, setLoading] = useState(false);
  const rel = relationships[targetUserId] || {};

  if (targetUserId === currentUserId) return null;

  const handleFollow = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/community/follow/${targetUserId}`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success(`Following ${targetName}`); onUpdate(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Failed to follow'); }
    finally { setLoading(false); }
  };

  const handleUnfollow = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/community/follow/${targetUserId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Unfollowed'); onUpdate(); }
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleFriendRequest = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/community/friends/request/${targetUserId}`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success(`Friend request sent to ${targetName}`); onUpdate(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleAcceptRequest = async () => {
    if (!rel.pending_request_id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/community/friends/accept/${rel.pending_request_id}`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success(`You and ${targetName} are now friends!`); onUpdate(); }
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleUnfriend = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/community/friends/${targetUserId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Unfriended'); onUpdate(); }
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const btnClass = compact ? "h-7 text-xs px-2" : "h-8 text-xs px-3";

  return (
    <div className="flex items-center gap-1.5" data-testid={`user-actions-${targetUserId}`}>
      {/* Follow/Unfollow */}
      {rel.is_following ? (
        <Button variant="outline" size="sm" className={`${btnClass} border-green-500 text-green-600 hover:bg-red-50 hover:text-red-600 hover:border-red-500 group`} onClick={handleUnfollow} disabled={loading} data-testid={`unfollow-btn-${targetUserId}`}>
          <Eye className="w-3 h-3 mr-1 group-hover:hidden" /><UserMinus className="w-3 h-3 mr-1 hidden group-hover:block" />
          <span className="group-hover:hidden">Following</span><span className="hidden group-hover:block">Unfollow</span>
        </Button>
      ) : (
        <Button size="sm" className={`${btnClass} bg-slate-100 text-slate-700 hover:bg-slate-200`} onClick={handleFollow} disabled={loading} data-testid={`follow-btn-${targetUserId}`}>
          <Eye className="w-3 h-3 mr-1" />Follow
        </Button>
      )}
      {/* Friend actions */}
      {rel.is_friend ? (
        <Button variant="outline" size="sm" className={`${btnClass} border-[#3D4F7A] text-[#3D4F7A] hover:bg-red-50 hover:text-red-600 hover:border-red-500 group`} onClick={handleUnfriend} disabled={loading} data-testid={`unfriend-btn-${targetUserId}`}>
          <UserCheck className="w-3 h-3 mr-1 group-hover:hidden" /><UserMinus className="w-3 h-3 mr-1 hidden group-hover:block" />
          <span className="group-hover:hidden">Friends</span><span className="hidden group-hover:block">Unfriend</span>
        </Button>
      ) : rel.pending_sent ? (
        <Button variant="outline" size="sm" className={`${btnClass} border-amber-400 text-amber-600`} disabled data-testid={`pending-btn-${targetUserId}`}>
          <Clock className="w-3 h-3 mr-1" />Pending
        </Button>
      ) : rel.pending_received ? (
        <Button size="sm" className={`${btnClass} bg-green-600 hover:bg-green-700 text-white`} onClick={handleAcceptRequest} disabled={loading} data-testid={`accept-btn-${targetUserId}`}>
          <Check className="w-3 h-3 mr-1" />Accept
        </Button>
      ) : (
        <Button size="sm" className={`${btnClass} bg-[#3D4F7A] hover:bg-[#2A3755] text-white`} onClick={handleFriendRequest} disabled={loading} data-testid={`add-friend-btn-${targetUserId}`}>
          <UserPlus className="w-3 h-3 mr-1" />Add Friend
        </Button>
      )}
    </div>
  );
}

function FriendBadge({ isFriend }) {
  if (!isFriend) return null;
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#3D4F7A]/10 text-[#3D4F7A] text-[10px] font-medium" data-testid="friend-badge">
      <UserCheck className="w-2.5 h-2.5" />Friend
    </span>
  );
}

function OnlineIndicator({ isOnline, size = "sm" }) {
  const sizeClass = size === "sm" ? "w-2.5 h-2.5" : "w-3 h-3";
  return (
    <span className={`${sizeClass} rounded-full ${isOnline ? 'bg-green-500' : 'bg-slate-300'} ring-2 ring-white block`} data-testid={`online-indicator-${isOnline ? 'online' : 'offline'}`} />
  );
}

function PostCard({ post, onLike, onDelete, currentUserId, onRefresh, relationships, onRelationshipUpdate }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyImage, setReplyImage] = useState(null);
  const [replyImagePreview, setReplyImagePreview] = useState(null);
  const replyFileInputRef = useRef(null);

  const isLiked = post.likes?.includes(currentUserId);
  const isAuthor = post.author_id === currentUserId;

  const loadComments = async () => {
    if (showComments && comments.length > 0) {
      setShowComments(false);
      return;
    }
    
    setLoadingComments(true);
    try {
      const response = await fetch(`${API}/community/posts/${post.post_id}/comments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoadingComments(false);
      setShowComments(true);
    }
  };

  const handleReplyImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPEG, PNG, GIF, or WebP.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max size is 5MB.');
      return;
    }
    
    setReplyImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setReplyImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearReplyImage = () => {
    setReplyImage(null);
    setReplyImagePreview(null);
    if (replyFileInputRef.current) replyFileInputRef.current.value = '';
  };

  const uploadReplyImage = async () => {
    if (!replyImage) return null;
    try {
      const formData = new FormData();
      formData.append('file', replyImage);
      const response = await fetch(`${API}/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        return data.image_url;
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    }
    return null;
  };

  const handleAddComment = async (parentId = null, content = newComment, includeImage = false) => {
    if (!content.trim() && !includeImage) return;
    
    setSubmittingComment(true);
    try {
      let imageUrl = null;
      if (includeImage && replyImage) {
        imageUrl = await uploadReplyImage();
      }
      
      const response = await fetch(`${API}/community/posts/${post.post_id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content, parent_id: parentId, image_url: imageUrl })
      });
      
      if (response.ok) {
        if (parentId) {
          setReplyText('');
          setReplyingTo(null);
          clearReplyImage();
          toast.success('Reply added!');
        } else {
          setNewComment('');
          toast.success('Comment added!');
        }
        // Reload comments
        const commentsRes = await fetch(`${API}/community/posts/${post.post_id}/comments`, {
          credentials: 'include'
        });
        if (commentsRes.ok) {
          setComments(await commentsRes.json());
        }
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      const response = await fetch(`${API}/community/comments/${commentId}/like`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        // Reload comments to reflect the change
        const commentsRes = await fetch(`${API}/community/posts/${post.post_id}/comments`, {
          credentials: 'include'
        });
        if (commentsRes.ok) {
          setComments(await commentsRes.json());
        }
      }
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={post.author_picture} alt={post.author_name} />
              <AvatarFallback className="bg-[#3D4F7A] text-white text-lg">
                {post.author_name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-900"><Link to={`/community/member/${post.author_id}`} className="hover:text-[#3D4F7A] hover:underline">{post.author_name}</Link> <FounderBadge tier={post.author_tier} /></p>
                <FriendBadge isFriend={relationships?.[post.author_id]?.is_friend} />
              </div>
              <p className="text-sm text-slate-500">{formatDate(post.created_at)}</p>
              {!isAuthor && (
                <div className="mt-1">
                  <UserActionButtons
                    targetUserId={post.author_id}
                    targetName={post.author_name}
                    currentUserId={currentUserId}
                    relationships={relationships || {}}
                    onUpdate={onRelationshipUpdate}
                    compact
                  />
                </div>
              )}
            </div>
          </div>
          
          {isAuthor && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={() => onDelete(post.post_id)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Post
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <p className="mt-4 text-slate-800 text-lg leading-relaxed whitespace-pre-wrap">{post.content}</p>

        {post.image_url && (
          <img 
            src={post.image_url} 
            alt="Post" 
            className="mt-4 rounded-xl max-h-96 w-full object-cover"
          />
        )}

        {/* Engagement Stats */}
        <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
          {post.likes?.length > 0 && (
            <span>{post.likes.length} {post.likes.length === 1 ? 'like' : 'likes'}</span>
          )}
          {post.comments_count > 0 && (
            <span>{post.comments_count} {post.comments_count === 1 ? 'comment' : 'comments'}</span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-around">
          <button 
            onClick={() => onLike(post.post_id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              isLiked 
                ? 'text-red-500 bg-red-50 font-medium' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
            Like
          </button>
          <button 
            onClick={loadComments}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              showComments 
                ? 'text-[#3D4F7A] bg-[#3D4F7A]/10 font-medium' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <MessageCircle className="w-5 h-5" />
            Comment
          </button>
          <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-all">
            <Share2 className="w-5 h-5" />
            Share
          </button>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            {loadingComments ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3D4F7A] mx-auto"></div>
              </div>
            ) : (
              <>
                {/* Comment Input */}
                <div className="flex gap-3 mb-4">
                  <Textarea 
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="resize-none min-h-[60px] flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                  />
                  <Button 
                    onClick={handleAddComment}
                    className="bg-[#3D4F7A] hover:bg-[#2A3755] self-end"
                    disabled={!newComment.trim() || submittingComment}
                    data-testid="submit-comment-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>

                {/* Comments List */}
                {comments.length > 0 && (
                  <div className="space-y-3">
                    {/* Get top-level comments (no parent) */}
                    {comments.filter(c => !c.parent_id).map((comment) => {
                      const isCommentLiked = comment.likes?.includes(currentUserId);
                      const replies = comments.filter(c => c.parent_id === comment.comment_id);
                      
                      return (
                        <div key={comment.comment_id}>
                          {/* Main Comment */}
                          <div className="flex gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={comment.author_picture} />
                              <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                                {comment.author_name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="bg-slate-50 rounded-xl p-3">
                                <p className="font-medium text-sm text-slate-900"><Link to={`/community/member/${comment.author_id}`} className="hover:text-[#3D4F7A] hover:underline">{comment.author_name}</Link> <FounderBadge tier={comment.author_tier} /></p>
                                <p className="text-sm text-slate-700 mt-1">{comment.content}</p>
                                {comment.image_url && (
                                  <img 
                                    src={comment.image_url} 
                                    alt="Comment" 
                                    className="mt-2 rounded-lg max-h-48 object-cover"
                                  />
                                )}
                              </div>
                              <div className="flex items-center gap-4 mt-1 ml-1">
                                <button 
                                  onClick={() => handleLikeComment(comment.comment_id)}
                                  className={`flex items-center gap-1 text-xs transition-colors ${
                                    isCommentLiked 
                                      ? 'text-[#3D4F7A] font-medium' 
                                      : 'text-slate-500 hover:text-[#3D4F7A]'
                                  }`}
                                  data-testid={`like-comment-${comment.comment_id}`}
                                >
                                  <ThumbsUp className={`w-3 h-3 ${isCommentLiked ? 'fill-current' : ''}`} />
                                  {comment.likes?.length > 0 ? comment.likes.length : 'Like'}
                                </button>
                                <button 
                                  onClick={() => setReplyingTo(replyingTo === comment.comment_id ? null : comment.comment_id)}
                                  className="text-xs text-slate-500 hover:text-[#3D4F7A] transition-colors"
                                  data-testid={`reply-btn-${comment.comment_id}`}
                                >
                                  Reply
                                </button>
                                <span className="text-xs text-slate-400">{formatDate(comment.created_at)}</span>
                              </div>
                              
                              {/* Reply Input */}
                              {replyingTo === comment.comment_id && (
                                <div className="mt-2 ml-4 space-y-2">
                                  {replyImagePreview && (
                                    <div className="relative inline-block">
                                      <img 
                                        src={replyImagePreview} 
                                        alt="Preview" 
                                        className="max-h-32 rounded-lg"
                                      />
                                      <button
                                        onClick={clearReplyImage}
                                        className="absolute -top-1 -right-1 p-0.5 bg-slate-900/70 hover:bg-slate-900 rounded-full text-white"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )}
                                  <div className="flex gap-2">
                                    <div className="flex-1 space-y-2">
                                      <Textarea 
                                        placeholder={`Reply to ${comment.author_name}...`}
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        className="resize-none min-h-[50px] text-sm"
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleAddComment(comment.comment_id, replyText, !!replyImage);
                                          }
                                        }}
                                      />
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="file"
                                          accept="image/jpeg,image/png,image/gif,image/webp"
                                          onChange={handleReplyImageSelect}
                                          ref={replyFileInputRef}
                                          className="hidden"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => replyFileInputRef.current?.click()}
                                          className="text-xs text-slate-500 hover:text-[#3D4F7A] flex items-center gap-1"
                                          data-testid="reply-add-image-btn"
                                        >
                                          <Image className="w-3 h-3" />
                                          Add Image
                                        </button>
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <Button 
                                        size="sm"
                                        onClick={() => handleAddComment(comment.comment_id, replyText, !!replyImage)}
                                        className="bg-[#3D4F7A] hover:bg-[#2A3755]"
                                        disabled={(!replyText.trim() && !replyImage) || submittingComment}
                                      >
                                        <Send className="w-3 h-3" />
                                      </Button>
                                      <Button 
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => { setReplyingTo(null); setReplyText(''); clearReplyImage(); }}
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Replies */}
                              {replies.length > 0 && (
                                <div className="mt-3 ml-4 space-y-2 border-l-2 border-slate-200 pl-3">
                                  {replies.map((reply) => {
                                    const isReplyLiked = reply.likes?.includes(currentUserId);
                                    return (
                                      <div key={reply.comment_id} className="flex gap-2">
                                        <Avatar className="w-6 h-6">
                                          <AvatarImage src={reply.author_picture} />
                                          <AvatarFallback className="bg-slate-200 text-slate-600 text-[10px]">
                                            {reply.author_name?.charAt(0)}
                                          </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1">
                                          <div className="bg-slate-100 rounded-lg p-2">
                                            <p className="font-medium text-xs text-slate-900"><Link to={`/community/member/${reply.author_id}`} className="hover:text-[#3D4F7A] hover:underline">{reply.author_name}</Link> <FounderBadge tier={reply.author_tier} size={12} /></p>
                                            <p className="text-xs text-slate-700 mt-0.5">{reply.content}</p>
                                            {reply.image_url && (
                                              <img 
                                                src={reply.image_url} 
                                                alt="Reply" 
                                                className="mt-1.5 rounded max-h-32 object-cover"
                                              />
                                            )}
                                          </div>
                                          <div className="flex items-center gap-3 mt-0.5 ml-1">
                                            <button 
                                              onClick={() => handleLikeComment(reply.comment_id)}
                                              className={`flex items-center gap-1 text-[10px] transition-colors ${
                                                isReplyLiked 
                                                  ? 'text-[#3D4F7A] font-medium' 
                                                  : 'text-slate-500 hover:text-[#3D4F7A]'
                                              }`}
                                            >
                                              <ThumbsUp className={`w-2.5 h-2.5 ${isReplyLiked ? 'fill-current' : ''}`} />
                                              {reply.likes?.length > 0 ? reply.likes.length : 'Like'}
                                            </button>
                                            <span className="text-[10px] text-slate-400">{formatDate(reply.created_at)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GroupCard({ group, onJoin, onLeave, isMember }) {
  return (
    <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3D4F7A] to-[#2A3755] flex items-center justify-center flex-shrink-0">
            <Users className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <Link to={`/community/groups/${group.group_id}`}>
              <h3 className="font-semibold text-slate-900 hover:text-[#3D4F7A] transition-colors">{group.name}</h3>
            </Link>
            <p className="text-sm text-slate-500">{group.member_count} {group.member_count === 1 ? 'member' : 'members'}</p>
            {group.description && (
              <p className="text-sm text-slate-600 mt-1 line-clamp-1">{group.description}</p>
            )}
          </div>
          {isMember ? (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onLeave(group.group_id)}
              className="border-green-500 text-green-600 hover:bg-green-50"
            >
              Joined ✓
            </Button>
          ) : (
            <Button 
              size="sm" 
              className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white"
              onClick={() => onJoin(group.group_id)}
              data-testid={`join-group-${group.group_id}`}
            >
              <Plus className="w-4 h-4 mr-1" />
              Join
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Community() {
  const { user, isActiveMember, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [feedFilter, setFeedFilter] = useState('all');
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [following, setFollowing] = useState([]);
  const [relationships, setRelationships] = useState({});
  const [showFindFriends, setShowFindFriends] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [communityStats, setCommunityStats] = useState({ online_count: 0, followers_count: 0 });
  const [showAllFriends, setShowAllFriends] = useState(false);

  const fetchFriends = useCallback(async () => {
    try {
      const res = await fetch(`${API}/community/friends`, { credentials: 'include' });
      if (res.ok) setFriends(await res.json());
    } catch (e) { console.error('Error fetching friends:', e); }
  }, []);

  const fetchFriendRequests = useCallback(async () => {
    try {
      const res = await fetch(`${API}/community/friends/requests`, { credentials: 'include' });
      if (res.ok) setFriendRequests(await res.json());
    } catch (e) { console.error('Error fetching friend requests:', e); }
  }, []);

  const fetchFollowing = useCallback(async () => {
    try {
      const res = await fetch(`${API}/community/following`, { credentials: 'include' });
      if (res.ok) setFollowing(await res.json());
    } catch (e) { console.error('Error fetching following:', e); }
  }, []);

  const fetchCommunityStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/community/stats`, { credentials: 'include' });
      if (res.ok) setCommunityStats(await res.json());
    } catch (e) { console.error('Error fetching community stats:', e); }
  }, []);

  const fetchRelationships = useCallback(async (userIds) => {
    const newRels = { ...relationships };
    const toFetch = userIds.filter(id => id !== user?.user_id && !newRels[id]);
    await Promise.all(toFetch.map(async (uid) => {
      try {
        const res = await fetch(`${API}/community/relationship/${uid}`, { credentials: 'include' });
        if (res.ok) newRels[uid] = await res.json();
      } catch {}
    }));
    setRelationships(newRels);
  }, [relationships, user?.user_id]);

  const refreshRelationships = useCallback(async () => {
    const allIds = Object.keys(relationships);
    const postAuthorIds = posts.map(p => p.author_id).filter(id => id !== user?.user_id);
    const uniqueIds = [...new Set([...allIds, ...postAuthorIds])];
    const newRels = {};
    await Promise.all(uniqueIds.map(async (uid) => {
      try {
        const res = await fetch(`${API}/community/relationship/${uid}`, { credentials: 'include' });
        if (res.ok) newRels[uid] = await res.json();
      } catch {}
    }));
    setRelationships(newRels);
    fetchFriends();
    fetchFriendRequests();
    fetchFollowing();
  }, [relationships, posts, user?.user_id, fetchFriends, fetchFriendRequests, fetchFollowing]);

  useEffect(() => {
    if (isActiveMember) {
      fetchPosts();
      fetchGroups();
      fetchFriends();
      fetchFriendRequests();
      fetchFollowing();
      fetchCommunityStats();
    } else {
      setLoading(false);
    }
  }, [isActiveMember]);

  useEffect(() => {
    if (posts.length > 0 && user?.user_id) {
      const authorIds = [...new Set(posts.map(p => p.author_id).filter(id => id !== user.user_id))];
      fetchRelationships(authorIds);
    }
  }, [posts, user?.user_id]);

  const fetchPosts = async (filter) => {
    const f = filter || feedFilter;
    try {
      const url = f === 'all' ? `${API}/community/posts` : `${API}/community/posts?feed=${f}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedFilterChange = (newFilter) => {
    setFeedFilter(newFilter);
    setLoading(true);
    fetchPosts(newFilter);
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const res = await fetch(`${API}/community/friends/accept/${requestId}`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        toast.success('Friend request accepted!');
        fetchFriendRequests();
        fetchFriends();
        refreshRelationships();
      }
    } catch { toast.error('Failed to accept'); }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      const res = await fetch(`${API}/community/friends/decline/${requestId}`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        toast.success('Request declined');
        fetchFriendRequests();
      }
    } catch { toast.error('Failed to decline'); }
  };

  const searchMembers = async (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API}/community/members/search?q=${encodeURIComponent(q)}`, { credentials: 'include' });
      if (res.ok) setSearchResults(await res.json());
    } catch {}
    finally { setSearching(false); }
  };

  const fetchGroups = async () => {
    try {
      const [allGroupsRes, myGroupsRes] = await Promise.all([
        fetch(`${API}/community/groups`, { credentials: 'include' }),
        fetch(`${API}/community/groups?my_groups=true`, { credentials: 'include' })
      ]);
      
      if (allGroupsRes.ok) setGroups(await allGroupsRes.json());
      if (myGroupsRes.ok) setMyGroups(await myGroupsRes.json());
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPEG, PNG, GIF, or WebP.');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max size is 5MB.');
      return;
    }
    
    setSelectedImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async () => {
    if (!selectedImage) return null;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedImage);
      
      const response = await fetch(`${API}/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.image_url;
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload image');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.trim() && !selectedImage) return;
    
    setPosting(true);
    try {
      let imageUrl = null;
      
      // Upload image if selected
      if (selectedImage) {
        imageUrl = await uploadImage();
        if (selectedImage && !imageUrl) {
          // Image was selected but upload failed
          setPosting(false);
          return;
        }
      }
      
      const response = await fetch(`${API}/community/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          content: newPost,
          image_url: imageUrl
        })
      });
      
      if (response.ok) {
        setNewPost('');
        setSelectedImage(null);
        setImagePreview(null);
        setShowCreatePost(false);
        toast.success('Post shared with the community!');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await fetch(`${API}/community/posts/${postId}/like`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const response = await fetch(`${API}/community/posts/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Post deleted');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      const response = await fetch(`${API}/community/groups/${groupId}/join`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('You joined the group!');
        fetchGroups();
      }
    } catch (error) {
      toast.error('Failed to join group');
    }
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      const response = await fetch(`${API}/community/groups/${groupId}/leave`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('You left the group');
        fetchGroups();
      }
    } catch (error) {
      toast.error('Failed to leave group');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A]"></div>
      </div>
    );
  }

  if (!isActiveMember) {
    return (
      <div className="h-screen relative overflow-hidden flex items-start justify-center pt-[12vh]">
        {/* Background community screenshot */}
        <div className="absolute inset-0">
          <img src="/images/community-preview.jpg" alt="" className="w-full h-full object-cover scale-105" />
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-[2px]"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center max-w-xl px-6">
          <div className="mx-auto mb-4 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center" style={{width: '5rem', height: '5rem'}}>
            <Lock className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-heading text-5xl sm:text-6xl font-bold text-white mb-3 whitespace-nowrap">Members Only Page</h2>
          <p className="text-xl text-slate-300 mb-1.5 leading-relaxed">
            A community where real estate professionals connect, share insights, and grow together.
          </p>
          <p className="text-xl font-semibold text-[#60a5fa] mb-6">Membership is FREE - join below to get access!</p>
          <Link to="/register">
            <Button size="lg" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-12 py-6 text-xl w-full shadow-lg shadow-blue-500/20 mb-3">
              Join AREA for Free
            </Button>
          </Link>
          <p className="text-base text-slate-400 mb-5">
            Already a member? <Link to="/login" className="text-[#60a5fa] font-medium hover:underline">Sign in here</Link>
          </p>
          <p className="text-base text-slate-400 mb-3">As a member, you'll get access to:</p>
          <div className="grid grid-cols-2 gap-3 text-base">
            <div className="flex items-center gap-2 text-slate-300">
              <span className="w-2.5 h-2.5 bg-[#60a5fa] rounded-full"></span>
              Social Feed & Posts
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <span className="w-2.5 h-2.5 bg-[#60a5fa] rounded-full"></span>
              Professional Groups
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <span className="w-2.5 h-2.5 bg-[#60a5fa] rounded-full"></span>
              Member Directory
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <span className="w-2.5 h-2.5 bg-[#60a5fa] rounded-full"></span>
              Exclusive Resources
            </div>
          </div>
        </div>
      </div>
    );
  }

  const myGroupIds = myGroups.map(g => g.group_id);
  const onlineFriends = friends.filter(f => f.is_online);
  const offlineFriends = friends.filter(f => !f.is_online);

  const FriendNameLink = ({ friend, className = "text-xs font-medium text-slate-800" }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`${className} truncate flex-1 text-left hover:underline cursor-pointer inline-flex items-center gap-1`} data-testid={`friend-name-${friend.user_id}`}>
          {friend.name} <FounderBadge tier={friend.membership_tier} size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-40">
        <DropdownMenuItem asChild>
          <Link to={`/community/messages?with=${friend.user_id}`} className="flex items-center gap-2 cursor-pointer">
            <MessageCircle className="w-3.5 h-3.5" />Message
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={`/community/member/${friend.user_id}`} className="flex items-center gap-2 cursor-pointer">
            <Eye className="w-3.5 h-3.5" />View Profile
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen bg-slate-100" data-testid="community-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#3D4F7A] to-[#2A3755] py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">Member Community</h1>
              <p className="text-slate-200 mt-1">Connect and collaborate with fellow AREA members</p>
            </div>
            <Link to="/community/messages">
              <Button className="bg-white/10 hover:bg-white/20 text-white border border-white/20" data-testid="messages-nav-btn">
                <MessageCircle className="w-4 h-4 mr-2" />Messages
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Sidebar - Friends & Groups */}
          <div className="lg:col-span-3 order-2 lg:order-1 space-y-4">
            {/* Friend Requests */}
            {friendRequests.length > 0 && (
              <Card className="border border-slate-200 shadow-sm" data-testid="friend-requests-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Bell className="w-4 h-4 text-[#D42B3C]" />
                    <h3 className="font-semibold text-slate-900 text-sm">Friend Requests</h3>
                    <span className="ml-auto bg-[#D42B3C] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{friendRequests.length}</span>
                  </div>
                  <div className="space-y-2">
                    {friendRequests.slice(0, 5).map((req) => (
                      <div key={req.request_id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50" data-testid={`friend-request-${req.request_id}`}>
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={req.from_user_picture} />
                          <AvatarFallback className="bg-[#3D4F7A] text-white text-xs">{req.from_user_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-900 truncate">{req.from_user_name}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" className="h-6 w-6 p-0 bg-green-600 hover:bg-green-700" onClick={() => handleAcceptRequest(req.request_id)} data-testid={`accept-request-${req.request_id}`}>
                            <Check className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-6 w-6 p-0 border-red-300 text-red-500 hover:bg-red-50" onClick={() => handleDeclineRequest(req.request_id)} data-testid={`decline-request-${req.request_id}`}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Friends List */}
            <Card className="border border-slate-200 shadow-sm" data-testid="friends-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => setShowAllFriends(!showAllFriends)}
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
                    data-testid="friends-title-btn"
                  >
                    <Users className="w-4 h-4 text-[#3D4F7A]" />
                    <h3 className="font-semibold text-slate-900 text-sm">Friends</h3>
                    {friends.length > 0 && <span className="text-xs text-slate-500">({friends.length})</span>}
                  </button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowFindFriends(!showFindFriends)} data-testid="find-friends-btn">
                    <Search className="w-3 h-3 mr-1" />Find
                  </Button>
                </div>

                {/* Find Friends Search */}
                {showFindFriends && (
                  <div className="mb-3" data-testid="find-friends-search">
                    <div className="relative mb-2">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search members..."
                        value={searchQuery}
                        onChange={(e) => searchMembers(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3D4F7A]/20 focus:border-[#3D4F7A]"
                        data-testid="member-search-input"
                      />
                    </div>
                    {searching && <p className="text-xs text-slate-500 text-center py-2">Searching...</p>}
                    {searchResults.length > 0 && (
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {searchResults.map((m) => (
                          <div key={m.user_id} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50" data-testid={`search-result-${m.user_id}`}>
                            <Avatar className="w-7 h-7">
                              <AvatarImage src={m.picture} />
                              <AvatarFallback className="bg-[#3D4F7A] text-white text-[10px]">{m.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-900 truncate"><Link to={`/community/member/${m.user_id}`} className="hover:text-[#3D4F7A] hover:underline">{m.name}</Link> <FounderBadge tier={m.membership_tier} size={12} /></p>
                              {m.user_role && <p className="text-[10px] text-slate-500 truncate">{m.user_role}</p>}
                            </div>
                            <UserActionButtons
                              targetUserId={m.user_id}
                              targetName={m.name}
                              currentUserId={user?.user_id}
                              relationships={relationships}
                              onUpdate={refreshRelationships}
                              compact
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {searchQuery && !searching && searchResults.length === 0 && (
                      <p className="text-xs text-slate-500 text-center py-2">No members found</p>
                    )}
                  </div>
                )}

                {/* All Friends List (toggle) */}
                {showAllFriends ? (
                  <div className="max-h-64 overflow-y-auto space-y-1" data-testid="all-friends-list">
                    {friends.length > 0 ? friends.map((f) => (
                      <div key={f.user_id} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50 transition-colors group">
                        <div className="relative">
                          <Avatar className="w-7 h-7">
                            <AvatarImage src={f.picture} />
                            <AvatarFallback className={`${f.is_online ? 'bg-[#3D4F7A] text-white' : 'bg-slate-200 text-slate-500'} text-[10px]`}>{f.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span className="absolute -bottom-0.5 -right-0.5"><OnlineIndicator isOnline={f.is_online} /></span>
                        </div>
                        <FriendNameLink friend={f} className={f.is_online ? "text-xs font-medium text-slate-800" : "text-xs text-slate-500"} />
                      </div>
                    )) : (
                      <p className="text-xs text-slate-500 text-center py-3">No friends yet. Use the search to find and add members!</p>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Online Friends */}
                    <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wider mb-1.5" data-testid="online-friends-label">Online - {onlineFriends.length}</p>
                    <div className="max-h-48 overflow-y-auto space-y-1" data-testid="online-friends-list">
                      {onlineFriends.length > 0 ? onlineFriends.map((f) => (
                        <div key={f.user_id} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50 transition-colors group" data-testid={`friend-online-${f.user_id}`}>
                          <div className="relative">
                            <Avatar className="w-7 h-7">
                              <AvatarImage src={f.picture} />
                              <AvatarFallback className="bg-[#3D4F7A] text-white text-[10px]">{f.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <span className="absolute -bottom-0.5 -right-0.5"><OnlineIndicator isOnline={true} /></span>
                          </div>
                          <FriendNameLink friend={f} />
                        </div>
                      )) : (
                        <p className="text-[10px] text-slate-400 py-2 text-center">No friends online</p>
                      )}
                    </div>
                  </>
                )}

                {friends.length === 0 && !showFindFriends && !showAllFriends && (
                  <p className="text-xs text-slate-500 text-center py-3 mt-2">No friends yet. Use the search to find and add members!</p>
                )}
              </CardContent>
            </Card>

            {/* Groups */}
            <Card className="border border-slate-200 shadow-sm sticky top-24">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900 text-sm">Groups</h3>
                  <Link to="/community/groups/create">
                    <Button size="sm" variant="outline" className="h-7 text-xs">
                      <Plus className="w-3 h-3 mr-1" />
                      Create
                    </Button>
                  </Link>
                </div>
                
                {myGroups.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">My Groups</p>
                    <div className="space-y-1.5">
                      {myGroups.slice(0, 3).map((group) => (
                        <Link 
                          key={group.group_id} 
                          to={`/community/groups/${group.group_id}`}
                          className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <div className="w-7 h-7 rounded-lg bg-[#3D4F7A]/10 flex items-center justify-center">
                            <Users className="w-3.5 h-3.5 text-[#3D4F7A]" />
                          </div>
                          <span className="text-xs font-medium text-slate-700 truncate">{group.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">Discover</p>
                  {groups.filter(g => !myGroupIds.includes(g.group_id)).length === 0 ? (
                    <p className="text-xs text-slate-500">No new groups to join</p>
                  ) : (
                    <div className="space-y-1.5">
                      {groups.filter(g => !myGroupIds.includes(g.group_id)).slice(0, 3).map((group) => (
                        <div key={group.group_id} className="flex items-center justify-between p-1.5 rounded-lg bg-slate-50">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-lg bg-[#3D4F7A] flex items-center justify-center flex-shrink-0">
                              <Users className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className="text-xs font-medium text-slate-700 truncate">{group.name}</span>
                          </div>
                          <Button 
                            size="sm" 
                            className="h-6 text-[10px] bg-[#3D4F7A] hover:bg-[#2A3755]"
                            onClick={() => handleJoinGroup(group.group_id)}
                          >
                            Join
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Feed - Center */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            {/* Feed Filter Tabs */}
            <div className="flex items-center gap-1 mb-4 bg-white rounded-xl p-1 border border-slate-200 shadow-sm" data-testid="feed-filter-tabs">
              {[
                { key: 'all', label: 'All Posts', icon: Users },
                { key: 'friends', label: 'Friends', icon: UserCheck },
                { key: 'friends_following', label: 'Friends & Following', icon: Eye },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => handleFeedFilterChange(key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    feedFilter === key
                      ? 'bg-[#3D4F7A] text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`feed-filter-${key}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {/* Create Post Card */}
            <Card className="border border-slate-200 shadow-sm mb-6">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#3D4F7A] text-white text-lg">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  
                  {showCreatePost ? (
                    <div className="flex-1">
                      <Textarea 
                        placeholder="What's on your mind? Share insights, ask questions, or start a discussion..."
                        value={newPost}
                        onChange={(e) => setNewPost(e.target.value)}
                        className="resize-none min-h-[120px] mb-3 text-base"
                        autoFocus
                        data-testid="post-input"
                      />
                      
                      {imagePreview && (
                        <div className="relative mb-3">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="max-h-64 rounded-xl object-cover"
                          />
                          <button
                            onClick={removeImage}
                            className="absolute top-2 right-2 p-1 bg-slate-900/70 hover:bg-slate-900 rounded-full text-white transition-colors"
                            data-testid="remove-image-btn"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleImageSelect}
                            ref={fileInputRef}
                            className="hidden"
                            data-testid="image-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingImage}
                            className="text-slate-600"
                            data-testid="add-image-btn"
                          >
                            <Image className="w-4 h-4 mr-2" />
                            Add Image
                          </Button>
                          <Button 
                            variant="ghost" 
                            onClick={() => {
                              setShowCreatePost(false);
                              setNewPost('');
                              removeImage();
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                        <Button 
                          onClick={handleCreatePost}
                          disabled={(!newPost.trim() && !selectedImage) || posting || uploadingImage}
                          className="bg-[#3D4F7A] hover:bg-[#2A3755]"
                          data-testid="post-submit"
                        >
                          {posting ? (uploadingImage ? 'Uploading...' : 'Posting...') : 'Post'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowCreatePost(true)}
                      className="flex-1 text-left px-4 py-3 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500 transition-colors"
                      data-testid="create-post-trigger"
                    >
                      What&apos;s on your mind, {user?.name?.split(' ')[0]}?
                    </button>
                  )}
                </div>
                
                {!showCreatePost && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex justify-center">
                    <Button 
                      onClick={() => setShowCreatePost(true)}
                      className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white px-6"
                      data-testid="create-post-btn"
                    >
                      <PenSquare className="w-4 h-4 mr-2" />
                      Create Post
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Posts Feed */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A] mx-auto"></div>
                <p className="text-slate-500 mt-4">Loading feed...</p>
              </div>
            ) : posts.length === 0 ? (
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-12 text-center">
                  <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                    {feedFilter === 'all' ? 'No posts yet' : feedFilter === 'friends' ? 'No friend posts yet' : 'No posts from friends or people you follow'}
                  </h3>
                  <p className="text-slate-500 mb-6">
                    {feedFilter === 'all' ? 'Be the first to share something with the community!' : 'Add friends or follow members to see their posts here!'}
                  </p>
                  {feedFilter === 'all' ? (
                    <Button onClick={() => setShowCreatePost(true)} className="bg-[#3D4F7A] hover:bg-[#2A3755]">
                      <PenSquare className="w-4 h-4 mr-2" />Create First Post
                    </Button>
                  ) : (
                    <Button onClick={() => { setShowFindFriends(true); }} className="bg-[#3D4F7A] hover:bg-[#2A3755]">
                      <Search className="w-4 h-4 mr-2" />Find Members
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <PostCard 
                    key={post.post_id} 
                    post={post} 
                    onLike={handleLike}
                    onDelete={handleDeletePost}
                    currentUserId={user?.user_id}
                    onRefresh={fetchPosts}
                    relationships={relationships}
                    onRelationshipUpdate={refreshRelationships}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-3 order-3">
            {/* Community Stats */}
            <Card className="border border-slate-200 shadow-sm mb-4">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-3 text-sm">Community</h3>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">Members Online</span>
                    <span className="font-semibold text-green-600 text-sm" data-testid="members-online-count">{communityStats.online_count}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">Active Groups</span>
                    <span className="font-semibold text-slate-900 text-sm">{groups.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">My Friends</span>
                    <span className="font-semibold text-slate-900 text-sm">{friends.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">Following</span>
                    <span className="font-semibold text-slate-900 text-sm">{following.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">Followed By</span>
                    <span className="font-semibold text-slate-900 text-sm" data-testid="followers-count">{communityStats.followers_count}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Following List */}
            {following.length > 0 && (
              <Card className="border border-slate-200 shadow-sm mb-4" data-testid="following-card">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-3 text-sm flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5 text-slate-500" />Following ({following.length})
                  </h3>
                  <div className="space-y-1.5">
                    {following.slice(0, 5).map((f) => (
                      <div key={f.user_id} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50">
                        <Avatar className="w-7 h-7">
                          <AvatarImage src={f.picture} />
                          <AvatarFallback className="bg-slate-200 text-slate-600 text-[10px]">{f.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <Link to={`/community/member/${f.user_id}`} className="text-xs font-medium text-slate-700 truncate hover:text-[#3D4F7A] hover:underline">{f.name}</Link> <FounderBadge tier={f.membership_tier} size={12} />
                      </div>
                    ))}
                    {following.length > 5 && <p className="text-[10px] text-slate-400 text-center">+{following.length - 5} more</p>}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Guidelines */}
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-3 text-sm">Community Guidelines</h3>
                <ul className="text-xs text-slate-600 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="text-[#3D4F7A]">•</span>Be respectful and professional
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3D4F7A]">•</span>Share valuable insights
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3D4F7A]">•</span>Support fellow members
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3D4F7A]">•</span>No spam or self-promotion
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3D4F7A]">•</span>Follow AREA Code of Ethics
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
